/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import { DropdownItemButton } from "../../common/DropdownItemButton";
import { Dropdown } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { DropdownMenuWrapper } from "../../common/DropdownMenuWrapper";
import { css } from "@emotion/react";
import { ellipsisStyle } from "../../menus/components/MenuDropdown";
import { Product } from "../models/product";
import { useUpdateProduct } from "../service/products-api";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { toastr } from "react-redux-toastr";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { getErrorMessage } from "../../../error-handler";

type Props = {
  setPackingItemToEditId: (id: string) => void;
  id: string;
  product: Product;
};

const dropdownInnerWrapper = css`
  display: flex;
  flex-direction: column;
  padding: 8px;
`;

export const ProductPackingItemsDropdown = ({
  setPackingItemToEditId,
  id,
  product,
}: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const [open, setOpen] = useState<boolean>(false);
  const { mutateAsync: updateProduct, isLoading } = useUpdateProduct(
    product.id
  );

  return (
    <Dropdown
      menu={{
        items: [
          {
            key: "1",
            label: (
              <DropdownItemButton
                onClick={() => {
                  setPackingItemToEditId(id);
                }}
                type="button"
                icon={<FontAwesomeIcon icon={solid("pen")} />}
              >
                Edit packing item
              </DropdownItemButton>
            ),
          },
          {
            key: "2",
            label: (
              <DropdownItemButton
                onClick={async () => {
                  try {
                    await updateProduct({
                      organizationKeyName,
                      data: {
                        ...product,
                        labels: (product.labels || []).map((l) => l.id),
                        tags: (product.tags || []).map((tag) => tag.id),
                        additionGroups: (product.additionGroups || []).map(
                          (group) => group.id
                        ),
                        productPackagingInstructions: (
                          product.packagingInstructions || []
                        )
                          .filter((pi) => pi.id !== id)
                          .map((pi) => ({
                            id: pi.id,
                            amount: pi.amount,
                          })),
                      },
                    });
                    toastr.success("Success", "Packing item removed");
                  } catch (error: unknown) {
                    const message = getErrorMessage(error);
                    toastr.error("Error", message);
                  }
                }}
                type="button"
                icon={
                  isLoading ? (
                    <ActionSpinner />
                  ) : (
                    <FontAwesomeIcon icon={solid("trash")} />
                  )
                }
                disabled={isLoading}
              >
                Remove packing item
              </DropdownItemButton>
            ),
          },
        ],
      }}
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      dropdownRender={(menu) => (
        <DropdownMenuWrapper>
          <div css={dropdownInnerWrapper}>
            {React.cloneElement(menu as React.ReactElement, {
              style: {
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                boxShadow: "none",
                padding: "0px",
                backgroundColor: "transparent",
              },
            })}
          </div>
        </DropdownMenuWrapper>
      )}
    >
      <div css={ellipsisStyle}>
        <FontAwesomeIcon icon={solid("ellipsis")} />
      </div>
    </Dropdown>
  );
};
